'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, SquarePlus, Share, EllipsisVertical } from 'lucide-react';
import { usePWADetection } from '@/utils/hooks/usePWADetection';
import { useConsent } from '@/components/consent-modal';
import { useTranslations } from 'next-intl';

export function InstallPrompt() {
  const { isStandalone, isIOS, isAndroid, isMobile, deferredPrompt } = usePWADetection();
  const hasConsent = useConsent();
  const [isVisible, setIsVisible] = useState(false);
  const t = useTranslations('installPrompt');

  useEffect(() => {
    // Don't show if consent hasn't been given yet
    if (!hasConsent) {
      return;
    }

    // If already in standalone mode, don't show anything
    if (isStandalone) {
      return;
    }

    // Auto-show after a delay for supported platforms
    if (deferredPrompt) {
      setTimeout(() => setIsVisible(true), 3000);
    } else if (isIOS || isAndroid) {
      setTimeout(() => setIsVisible(true), 5000);
    }
  }, [isStandalone, deferredPrompt, isIOS, isAndroid, hasConsent]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsVisible(false);
      }
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show if:
  // - Already installed
  // - Not supported
  // - User dismissed in this session
  // - Consent hasn't been given yet
  if (
    isStandalone ||
    (!deferredPrompt && !isIOS && !isAndroid) ||
    !isVisible ||
    !hasConsent ||
    sessionStorage.getItem('pwa-install-dismissed')
  ) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm">
      <div className="bg-background border border-border rounded-lg shadow-lg p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <Download className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-sm">{t('title')}</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <p className="text-sm text-muted-foreground">
          {t('description', { platform: isMobile ? t('mobile') : t('desktop') })}
        </p>

        <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'space-x-2'}`}>
          {deferredPrompt ? (
            // Chrome, Edge, etc.
            <>
              <Button onClick={handleInstallClick} className="flex-1" size="sm">
                <Download className="h-4 w-4 mr-2" />
                {t('installApp')}
              </Button>
              <Button variant="outline" onClick={handleDismiss} size="sm">
                {t('maybeLater')}
              </Button>
            </>
          ) : isIOS ? (
            // iOS Safari
            <>
              <div className="text-sm">
                <p className="font-medium mb-2">{t('toInstall')}:</p>
                <div className="space-y-1 text-muted-foreground">
                  <p className="flex flex-wrap items-center gap-1">
                    <span>{t('ios.step1')}</span>
                    <Share className="h-4 w-4" />
                    <span>{t('ios.share')}</span>
                  </p>
                  <p className="flex flex-wrap items-center gap-1">
                    <span>{t('ios.step2')}</span>
                    <span>{t('ios.addToHomeScreen')}</span>
                    <SquarePlus className="h-4 w-4" />
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={handleDismiss} size="sm">
                {t('maybeLater')}
              </Button>
            </>
          ) : isAndroid ? (
            // Android browsers
            <>
              <div className="text-sm">
                <p className="font-medium mb-2">{t('toInstall')}:</p>
                <div className="space-y-1 text-muted-foreground">
                  <p className="flex flex-wrap items-center gap-1">
                    <span>{t('android.step1')}</span>
                    <EllipsisVertical className="h-4 w-4" />
                  </p>
                  <p className="flex flex-wrap items-center gap-1">
                    <span>{t('android.step2')}</span>
                    <span>{t('android.addToHomeScreen')}</span>
                    <SquarePlus className="h-4 w-4" />
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={handleDismiss} size="sm">
                {t('maybeLater')}
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
